import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate  } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Button from "components/common/Button";
import Popup from "components/common/Popup";

import styles from "./styles.module.scss";

const Privacy = ({ previousLocation }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const lang = useSelector((state) => state.settings.lang);

    const handleClose = useCallback(() => {
        navigate(previousLocation);
    }, [navigate, previousLocation]);

    return (
        <Popup title={t("footer.policy")} onClose={handleClose}>
            <div className={styles.privacy}>
                {lang === "en" ? (
                    <>
                        <p>
                            BLAGO OÜ (“BLAGO”) provides an online platform to connect Shippers with
                            Carriers for the transportation of cargo. Shippers can submit a request
                            for the transportation of cargo (“Shipment”), Carriers can accept such
                            requests and assign Drivers to fulfill them. To provide Services, BLAGO
                            collects and processes various categories of data. This Privacy Notice
                            describes how BLAGO (“we” or “us”or“our”) collects, uses, and discloses
                            personal information when you use our websites, mobile applications, and
                            other online products and services (collectively, the “Services”) or
                            when you otherwise interact with us.
                        </p>
                        <p>
                            We may change this Privacy Notice from time to time. If we make changes,
                            we will notify you by revising the Effective Date at the top of this
                            Privacy Notice, and, in some cases, we may provide you with additional
                            notice (such as adding a statement to our homepage or sending you a
                            notification on our mobile app or via email). We encourage you to review
                            this Privacy Policy whenever you access the Services or otherwise
                            interact with us to stay informed about our information practices and
                            the choices available to you.
                        </p>
                        <p>
                            While using our Services, we may ask you to provide us with certain
                            personally identifiable information that can be used to identify you
                            (“Personal Data”).
                        </p>
                        <h2>I. Collection of Personal Data</h2>
                        <h3>Information You Provide to Us</h3>
                        <p>
                            We collect information you provide directly to us. For example, we
                            collect information when you fill out a form, request to be a Carrier or
                            Shipper, log in to your BLAGO account, submit or accept a Shipment
                            request, request customer support, or otherwise communicate with us.
                            This information may include name, company name, company number, email
                            address, phone number, address, financial information, shipment
                            information, service ratings and reviews, and any other information you
                            choose to provide.
                        </p>
                        <h3>Information We Collect When You Use the Services</h3>
                        <p>
                            When you use the Services, we collect additional information about
                            Shipment requests and how they are fulfilled, including the following:
                        </p>
                        <p>
                            <strong>Shippers:</strong> If you are a Shipper and submit a Shipment
                            request, we collect certain details related to your submission,
                            including the date and time of your request, origin and destination
                            addresses, description of the cargo being transported, rate, and other
                            information about the Shipment.
                        </p>
                        <p>
                            <strong>Carriers:</strong> If you are a Carrier, we collect information
                            on fulfillment of any Shipment requests, including financial, vehicle,
                            and driver information.
                        </p>
                        <p>
                            <strong>Drivers:</strong> If you are a driver transporting cargo on
                            behalf of a Carrier, we may collect information on your travel to
                            fulfill a Shipment request. We also collect information about your
                            location and about documents you upload, as described below.
                        </p>
                        <h3>Location Information</h3>
                        <p>
                            Providing Services to our users may require the collection of precise
                            location information. We may also derive your approximate location from
                            your IP address. We may also receive information about your location
                            from third parties, such as the Shippers and Carriers involved in
                            fulfilling any Shipment request, or from sensors installed on trailers
                            owned, leased, or controlled by BLAGO.
                        </p>
                        <h3>Photographs and Documents</h3>
                        <p>
                            Our Services may ask you to photograph and upload to the Services
                            certain documents related to the fulfillment of a Shipment request, such
                            legal documents, bills of lading, and proofs of delivery. You may also
                            be able to upload pictures of cargo that you carry and/or trailers
                            owned, leased, or controlled by BLAGO.
                        </p>
                        <h3>Information We Collect Automatically When You Use the Services</h3>
                        <p>
                            When you access or use our Services, we automatically collect
                            information about you, including:
                        </p>
                        <p>
                            <strong>Log Information:</strong> We collect log information about your
                            use of the Services, including the type of browser or mobile app version
                            you use, access times, pages viewed, your IP address, and the page you
                            visited before and after navigating to our Services.
                        </p>
                        <p>
                            <strong>Device Information:</strong> We collect information about the
                            computer or mobile device you use to access our Services, including the
                            hardware model, operating system and version, mobile device and mobile
                            app identifiers, and mobile network information.
                        </p>
                        <p>
                            <strong>
                                Information Collected by Cookies and Other Tracking Technologies:
                            </strong>
                            We use various technologies to collect information, including cookies
                            and web beacons. Cookies are small data files stored on your hard drive
                            or in device memory that help us improve our Services and your
                            experience, see which areas and features of our Services are popular,
                            and count visits. Web beacons are electronic images that may be used in
                            our Services or emails and help deliver cookies, count visits, and
                            understand usage and campaign effectiveness. You may instruct your
                            browser to block cookies but doing so may limit your ability to utilize
                            all features of our Services.
                        </p>
                        <h2>II. Use of Personal Data</h2>
                        <p>
                            We use the information we collect in order to provide and improve our
                            Services, including to connect Shippers with Carriers, match Drivers
                            with Shipment requests, facilitate transportation of cargo, process
                            payments, and confirm delivery. We may also use information about you
                            for other purposes, including to:
                        </p>
                        <ul>
                            <li>
                                Send you technical notices, updates, security alerts and support,
                                and administrative messages;
                            </li>
                            <li>
                                Respond to your comments, questions, and requests and provide
                                customer service;
                            </li>
                            <li>
                                Communicate with you about products, services, offers,
                                opportunities, promotions, rewards, and events offered by BLAGO and
                                others, and provide news and information we think will be of
                                interest to you;
                            </li>
                            <li>
                                Monitor and analyze trends, usage, and activities in connection with
                                our Services;
                            </li>
                            <li>
                                Detect, investigate, and prevent fraudulent transactions and other
                                illegal activities and protect the rights and property of BLAGO and
                                others;
                            </li>
                            <li>
                                Personalize and improve Services and provide advertisements,
                                content, or features that match user profiles or interests;
                            </li>
                            <li>
                                Link or combine with information we get from others and create data
                                profiles to personalize our Services to better understand your needs
                                and provide you with better service; and
                            </li>
                            <li>
                                Carry out any other purpose described to you at the time the
                                information was collected.
                            </li>
                        </ul>
                        <h3>Security</h3>
                        <p>
                            BLAGO takes reasonable measures to help protect information about you
                            from loss, theft, misuse and unauthorized access, disclosure,
                            alteration, and destruction.
                        </p>
                        <h2>III. Sharing of Personal Data</h2>
                        <p>
                            We share information we collect about you, as described in this Privacy
                            Notice or as at the time of collection or sharing, including sharing
                            with other users of our Services and various third-parties as described
                            below. BLAGO does not sell personal information, as defined by the
                            California Consumer Privacy Act (“CCPA”).
                        </p>
                        <h3>Sharing with Other Users of our Services</h3>
                        <p>
                            Depending on how you use our Services, we may share information about
                            you with other users of the Services. For example, if you are a driver,
                            we share your name, vehicle information, precise or approximate
                            location, and certain contact information (including phone number) with
                            Shippers involved in the Shipment request you helped to fulfill. If you
                            are a Shipper your name or company name, company number, email address,
                            your instructions, address, and contact information will be shared with
                            drivers who will transport your cargo. Certain documents and information
                            that you submit to our Services in connection with a Shipment request,
                            such as feedback, Shipment, and payment history with BLAGO, may be
                            shared with other users. If you submit driver ratings and reviews, this
                            information may be shared with other users of our Services.
                        </p>
                        <p>
                            We may also share information about you with other third parties, such
                            as:
                        </p>
                        <ul>
                            <li>
                                With vendors, consultants, Partners, and other service providers who
                                need access to such information to carry out work on our behalf;
                            </li>
                            <li>
                                In response to a request for information if we believe disclosure is
                                in accordance with, or required by, any applicable law, regulation
                                or legal process;
                            </li>
                            <li>
                                If we believe your actions are inconsistent with our user agreements
                                or policies, or to protect the rights, property, and safety of BLAGO
                                or others, with law enforcement or other entities and individuals;
                            </li>
                            <li>
                                In connection with, or during negotiations of, any merger, sale of
                                company assets, financing, or acquisition of all or a portion of our
                                business by another company;
                            </li>
                            <li>
                                Between and among BLAGO and our current and future parents,
                                affiliates, subsidiaries, and other companies under common control
                                and ownership;
                            </li>
                            <li> With your consent or at your direction; </li>
                            <li>
                                We may also share aggregated or de-identified information, which
                                cannot reasonably be used to identify you.
                            </li>
                        </ul>
                        <h2>IV. Advertising and Analytics Services</h2>
                        <p>
                            We may allow others to provide advertising and analytics services on our
                            behalf across the Internet and in applications. These services may use
                            cookies, web beacons, device identifiers, and other technologies to
                            collect information related to your use of the Service, including your
                            IP address, web browser, mobile network information, pages viewed, time
                            spent on pages or in apps, links clicked, and conversion information.
                        </p>
                        <h2>V. Your Data Rights </h2>
                        <p>
                            Depending on the applicable laws and regulations, you may have the
                            following rights regarding your personal data held by BLAGO:
                        </p>
                        <ul>
                            <li>
                                The Right to Know: You may have the right to know categories of
                                personal information BLAGO collects about you, which is also
                                provided in the “Collection of Information” section above.
                            </li>
                            <li>
                                The Right of Access: You may have the right to request the
                                categories of (i) personal information we have collected about you,
                                (ii) sources from which the personal information is collected, (iii)
                                purposes of such collection, (iv) third parties with whom we share
                                personal information, and (v) personal information we shared about
                                you and the reasons we shared it.
                            </li>
                            <li>
                                The Right to Delete: You may have the right to request we delete
                                personal information collected from you.
                            </li>
                            <li>
                                The Right to Opt-Out of Data Sale: BLAGO does not “sell” your
                                personal information, as defined by the CCPA.
                            </li>
                            <li>
                                The Right of Private Action: You may have a private right of action
                                if you are negatively affected by a data breach, subject to the
                                applicable laws.
                            </li>
                            <li>
                                Right to Data Portability: You may have the right to request to
                                move, copy, or transfer your personal data held by BLAGO in a
                                machine-readable format.
                            </li>
                        </ul>
                        <p>
                            BLAGO may process requests relating to personal information collected
                            over the preceding 12-month period, as legally required, and we may
                            verify your identity, or an agent’s authorization to act on your behalf,
                            prior to responding to your request. BLAGO does not discriminate against
                            you for exercising your data rights. To submit a request, please contact
                            us using the information provided below in Section VII “Contact Us”.
                        </p>
                        <h2>VI. Applicable to EU Data Subjects </h2>
                        <p>
                            BLAGO, as a data controller, generally relies on the following legal
                            bases to process personal data:
                        </p>
                        <ol>
                            <li>
                                <strong>Performance of the Services.</strong> The use of Your
                                information may be necessary to perform the Services. For example,
                                if You utilize the BLAGO website, We will use Your information to
                                carry out Our contractual obligations or to provide those services.
                            </li>
                            <li>
                                <strong>Legitimate interests.</strong> We use Your information for
                                our legitimate interests, such as to provide You with the best
                                content through Our website and communications with users and the
                                public, to improve and promote Our products and services, and for
                                administrative, security, fraud prevention and legal purposes.
                            </li>
                            {/* <li>
                                <strong>Consent.</strong> We may rely on Your consent to use Your
                                personal information for certain direct marketing purposes, such as
                                sending You updates about our products and services. You may
                                withdraw Your consent at any time through the unsubscribe feature
                                provided with each marketing email or by contacting us at
                                blagotruns@gmail.com. You understand that by withdrawing Your
                                consent, We may no longer be able to provide Our products or perform
                                certain aspects of the services, and, in this instance, You agree
                                not to file suit against Us, or make a claim or complaint against us
                                for Our failure to perform.
                            </li> */}
                            <li>
                                <strong>To Comply with a Legal Obligation.</strong> We may also use
                                your information as necessary to comply with applicable laws and
                                regulations.
                            </li>
                        </ol>
                        <p>
                            In addition, EU Data Subjects may be entitled to the following rights
                            with regard to Your personal information and data:
                        </p>
                        <ul>
                            <li>
                                Right of access to Your personal data, to know what information
                                about You We hold
                            </li>
                            <li>
                                Right to correct any incorrect or incomplete personal data about
                                Yourself that We hold
                            </li>
                            <li>Right to restrict/suspend our processing of Your personal data</li>
                            <li>
                                Right to complain to a supervisory authority if you believe your
                                privacy rights are being violated
                            </li>
                        </ul>
                        <p>Additional rights that may apply to You in certain instances: </p>
                        <ul>
                            <li>
                                Right of data portability (if Our processing is based on consent and
                                automated means)
                            </li>
                            <li>
                                Right to withdraw consent at any time (if processing is based on
                                consent)
                            </li>
                            <li>
                                Right to object to processing (if processing is based on legitimate
                                interests)
                            </li>
                            <li>
                                Right to object to processing of Your personal data for direct
                                marketing purposes
                            </li>
                            <li>
                                Right of erasure of Your personal data from our system (“right to be
                                forgotten”) if certain grounds are met
                            </li>
                        </ul>
                        {/* <p>
                            For EU data subjects to exercise privacy rights, they can email Us at
                            blagotruns@gmail.com with “EU Data Subjects- Privacy” in the subject
                            line.
                        </p> */}
                        <p>
                            We generally retain personal information as long as necessary for the
                            purposes for which it is collected, provided that We may need to retain
                            certain personal information after such purposes are fulfilled to comply
                            with legal and accounting obligations.
                        </p>
                        <p>
                            We may, from time to time, share Your personal data with Our service
                            providers to the extent necessary to deliver the services that You
                            request of us. Before doing so, We shall enter into a written agreement
                            with them with terms no less onerous than appearing in this policy.
                            Prior to transferring Your personal data to another country, to the
                            extent required under the GDPR, We shall utilize appropriate safeguards
                            as required by Article 46 of the GDPR. To the extent applicable, such
                            countries to which we transfer personal data will provide an essentially
                            equivalent level of protection to that the personal data of EU Data
                            Subjects receives when it is being processed within the EU, or we will
                            implement supplemental measures to provide the same.
                        </p>
                        <p>
                            We do not employ automated decision making in Our processing of Your
                            personal data.
                        </p>
                        {/* <h2>VI. Contact Us</h2>
                        <p>
                            For further information about BLAGO’s Privacy Notice or to submit a data
                            subject request, please contact blagotruns@gmail.com.
                        </p> */}
                    </>
                ) : (
                    <>
                        <p>
                            Компания BLAGO OÜ (“BLAGO”) предоставляет онлайн-платформу, которая
                            обеспечивает взаимодействие между Грузоотправителями и Перевозчиками в
                            целях осуществления перевозки грузов. С помощью данной платформы
                            Грузоотправители могут подавать заявки на перевозку грузов (далее –
                            <strong>«Грузоперевозка»</strong>), а Перевозчики могут принимать такие
                            заявки и назначать Водителей для их выполнения. В целях предоставления
                            Сервисов компания BLAGO собирает и обрабатывает различные категории
                            данных. Настоящая Политика конфиденциальности содержит описание процесса
                            сбора, использования и раскрытия компанией BLAGO (далее – «мы», «нас»
                            или «наш») персональных данных при использовании вами наших сайтов,
                            мобильных приложений, прочих онлайн-продуктов и сервисов (далее
                            совместно – «Сервисы») или при ином вашем взаимодействии с нами.
                        </p>
                        <p>
                            Мы можем по мере необходимости вносить изменения в настоящее Уведомление
                            о конфиденциальности. В случае внесения изменений мы уведомим вас об
                            этом, изменив дату вступления в силу, указанную в верхней части
                            Уведомления о конфиденциальности, а также в некоторых случаях мы можем
                            направить вам дополнительное уведомление (например, разместив
                            соответствующее заявление на домашней странице или отправив уведомление
                            на ваше мобильное приложение или на ваш адрес электронной почты). Мы
                            рекомендуем вам просматривать настоящую Политику конфиденциальности при
                            каждом доступе к Сервисам или ином взаимодействии с нами, чтобы обладать
                            информацией о наших методах работы с данными и имеющихся у вас
                            возможностях выбора.
                        </p>
                        <p>
                            Во время использования наших Сервисов мы можем попросить вас
                            предоставить нам идентифицирующую личность информацию, которая может
                            быть использована для установления вашей личности (далее – «Персональные
                            данные»). 
                        </p>
                        <h2>I. Сбор персональных данных</h2>
                        <h3>Информация, которую вы предоставляете нам</h3>
                        <p>
                            Мы собираем информацию, которую вы напрямую предоставляете нам.
                            Например, мы собираем информацию, когда вы заполняете формы, направляете
                            запрос о присвоении статуса Перевозчика или Грузоотправителя, входите в
                            свою учетную запись BLAGO, отправляете или принимаете запрос на
                            Грузоперевозку, запрашиваете содействие службы поддержки или иным
                            образом взаимодействуете с нами. Данная информация может содержать имя,
                            название компании, номер компании, адрес электронной почты, номер
                            телефона, адрес, финансовую информацию, информацию о грузоперевозках,
                            рейтинги сервисов и отзывы, а также любые другие сведения, добровольно
                            предоставленные вами.
                        </p>
                        <h3>Информация, которую мы собираем при использовании вами Сервисов</h3>
                        <p>
                            При использовании вами Сервисов мы собираем дополнительную информацию о
                            запросах на Грузоперевозку и об их выполнении, включая следующие
                            сведения:
                        </p>
                        <p>
                            <strong>Грузоотправители.</strong> Если вы являетесь Грузоотправителем и
                            отправляете запрос на Грузоперевозку, мы собираем сведения о вашем
                            запросе, включая сведения о дате и времени вашего запроса, адресе
                            отправления и назначения груза, ставке, а также описание перевозимого
                            груза и прочие сведения о Грузоперевозке.
                        </p>
                        <p>
                            <strong>Перевозчики.</strong> Если вы являетесь Перевозчиком, мы
                            собираем информацию о выполнении любых запросов на Грузоперевозку,
                            включая финансовую информацию, сведения о транспортном средстве и
                            водителе.
                        </p>
                        <p>
                            <strong>Водители.</strong> Если вы являетесь водителем, который
                            перевозит груз от имени Перевозчика, мы можем собирать информацию о
                            вашем маршруте для выполнения запроса на Грузоперевозку. Мы также
                            собираем сведения о вашем местонахождении и загружаемых вами документах,
                            как описано ниже.
                        </p>
                        <h3>Сведения о местонахождении</h3>
                        <p>
                            Для предоставления Сервисов нашим пользователям нам могут потребоваться
                            точные сведения о местонахождении. Мы также можем определить ваше
                            ориентировочное местоположение по вашему IP-адресу. Кроме этого, мы
                            также можем получать сведения о вашем местонахождении от третьих лиц,
                            таких как Грузоотправители и Перевозчики, принимающие участие в
                            выполнении какого-либо запроса на Грузоперевозку, или от датчиков,
                            установленных на грузовых фурах, принадлежащих компании BLAGO,
                            арендуемых ею или находящихся под ее управлением.
                        </p>
                        <h3>Фотографии и документы</h3>
                        <p>
                            Наши Сервисы могут запросить вас сфотографировать и загрузить в Сервисы
                            определенные документы, относящиеся к выполнению запроса на
                            Грузоперевозку, такие как юридические документы, коносаменты и
                            подтверждение доставки груза. Вы также можете загружать фотографии
                            перевозимого вами груза и (или) грузовых фур, принадлежащих компании
                            BLAGO, арендуемых ею или находящихся под ее управлением.
                        </p>
                        <h3>
                            Информация, которую мы собираем автоматически при использовании вами
                            наших Сервисов
                        </h3>
                        <p>
                            При осуществлении вами доступа к нашим Сервисам или при их
                            использовании, мы автоматически собираем информацию о вас, в том числе:
                        </p>
                        <p>
                            <strong>Сведения из журналов событий.</strong> Мы собираем сведения из
                            журналов событий о вашем использовании Сервисов, включая сведения о типе
                            используемого вами браузера или версии мобильного приложения, времени
                            осуществления доступа, просмотренных страницах, вашем IP-адресе и
                            странице, которую вы посетили до и после перехода к нашим Сервисам.
                        </p>
                        <p>
                            <strong>Сведения об устройстве.</strong> Мы собираем сведения о
                            компьютере или мобильном устройстве, которое вы используете для
                            осуществления доступа к нашим Сервисам, включая сведения о модели
                            аппаратных средств, операционной системе и версии, идентификаторах
                            мобильных устройств и мобильных приложений, а также сведения о мобильной
                            сети.
                        </p>
                        <p>
                            <strong>
                                Информация, собираемая с помощью файлов cookie и других технологий
                                отслеживания.
                            </strong>
                            Мы используем различные технологии для сбора информации, включая файлы
                            cookie и веб-маяки. Файлы cookie – это небольшие файлы данных,
                            хранящиеся на вашем жестком диске или в памяти устройства, которые
                            помогают нам улучшать наши Сервисы и вашу удовлетворенность ими,
                            узнавать, какие области и функции наших Сервисов пользуются
                            популярностью, и подсчитывать количество посещений. Веб-маяки
                            представляют собой электронные изображения, которые могут использоваться
                            в наших Сервисах или сообщениях электронной почты и помогают доставлять
                            файлы cookie, в том числе они позволяют подсчитывать количество
                            посещений и получать данные об использовании и эффективности рекламных
                            кампаний. Вы можете настроить свой браузер на блокировку файлов cookie,
                            однако, это может привести к частичному ограничению использования
                            функциональных возможностей наших Сервисов.
                        </p>
                        <h2>II. Использование персональных данных</h2>
                        <p>
                            Мы используем собираемую нами информацию для предоставления наших
                            Сервисов и улучшения их качества, в том числе для связи
                            Грузоотправителей с Перевозчиками, подбора Водителей для запросов на
                            Грузоперевозку, упрощения Грузоперевозки, обработки платежей и
                            подтверждения доставки груза. Мы также можем использовать информацию о
                            вас для иных целей, в том числе, для того чтобы:
                        </p>
                        <ul>
                            <li>
                                отправлять Вам технические уведомления, обновления, предупреждения
                                безопасности, а также сообщения поддержки и административные
                                сообщения;
                            </li>
                            <li>
                                отвечать на ваши комментарии, вопросы и запросы, а также
                                предоставлять клиентскую поддержку;
                            </li>
                            <li>
                                сообщать вам о продуктах, услугах, предложениях, возможностях,
                                рекламных акциях, вознаграждениях и событиях, предлагаемых BLAGO и
                                другими компаниями, и предоставлять новости и информацию, которые,
                                по нашему мнению, могут заинтересовать вас;
                            </li>
                            <li>
                                осуществлять мониторинг и анализ тенденций, использования и
                                действий, связанных с нашими Сервисами;
                            </li>
                            <li>
                                выявлять, расследовать и предотвращать мошеннические операции и
                                другие незаконные действия и защищать права и собственность BLAGO и
                                других компаний;
                            </li>
                            <li>
                                персонализировать и улучшать наши Сервисы и предоставлять рекламу,
                                контент или функционал, соответствующий профилям или интересам
                                пользователей;
                            </li>
                            <li>
                                Связывать или объединять информацию, которую мы получаем от третьих
                                других, и создавать профили данных для индивидуализации наших
                                Сервисов, для лучшего понимания ваших потребностей и предоставления
                                вам более качественных услуг; а также
                            </li>
                            <li>
                                выполнять любые иные цели, о которых Вас проинформировали во время
                                сбора информации.
                            </li>
                        </ul>
                        <h3>Безопасность</h3>
                        <p>
                            BLAGO принимает надлежащие меры в целях предотвращения утраты, кражи,
                            неправомерного использования, раскрытия, изменения и уничтожения
                            информации о вас, а также предотвращения несанкционированного доступа к
                            такой информации.
                        </p>
                        <h2>III. Предоставление персональных данных</h2>
                        <p>
                            Мы предоставляем собранную о вас информацию в порядке, предусмотренном
                            настоящим Уведомлением о конфиденциальности, или по состоянию на момент
                            ее сбора или передачи, включая передачи другим пользователям наших
                            Сервисов и иным третьим лицам, как описано ниже. BLAGO не занимается
                            продажей персональных данных как это описано в определении,
                            предусмотренном Законом Калифорнии о защите персональных данных
                            потребителей («CCPA»).
                        </p>
                        <h3>Предоставление информации о вас другим пользователям наших Сервисов</h3>
                        <p>
                            В зависимости от того, как вы используете наши Сервисы, мы можем
                            предоставлять информацию о вас другим пользователям наших Сервисов.
                            Например, если вы являетесь Водителем, мы сообщаем ваше имя, сведения о
                            транспортном средстве, точное или ориентировочное местоположение и
                            определенную контактную информацию (включая номер телефона)
                            Грузоотправителям по запросу на Грузоперевозку, который вы помогли
                            выполнить. Если вы являетесь Грузоотправителем, мы сообщаем ваше имя или
                            наименование компании, номер компании, адрес электронной почты, ваши
                            инструкции, адрес и контактную информацию водителям, которые будут
                            перевозить ваш груз. Некоторые документы и сведения, которые вы
                            передаете нашим Сервисам в связи с запросом на Грузоперевозку, например,
                            обратная связь, Грузоперевозка и история платежей с компанией BLAGO,
                            могут быть переданы другим пользователям. Если вы предоставляете
                            рейтинги водителей и обзоры, такие сведения также могут быть переданы
                            другим пользователям наших Сервисов.
                        </p>
                        <p>
                            Мы также можем предоставлять информацию о вас третьим лицам, в том
                            числе:
                        </p>
                        <ul>
                            <li>
                                поставщикам, консультантам, партнерам и другим поставщикам услуг,
                                которым необходим доступ к такой информации для выполнения работ от
                                нашего имени;
                            </li>
                            <li>
                                в ответ на запрос информации, если мы считаем, что раскрытие
                                информации соответствует требованиям действующего законодательства,
                                регламента или судебного порядка;
                            </li>
                            <li>
                                если мы посчитаем, что ваши действия противоречат условиям наших
                                пользовательских соглашений или политик; либо для защиты прав,
                                имущества и безопасности компании BLAGO или других лиц, в связи с
                                деятельностью правоохранительных органов или иных юридических и
                                физических лиц;
                            </li>
                            <li>
                                в связи со слиянием, продажей активов компании, привлечением
                                инвестиций, или приобретением всего или части нашего предприятия
                                другой компанией, или в ходе проведения переговоров в отношении
                                вышеизложенного;
                            </li>
                            <li>
                                между BLAGO и нашими нынешними и будущими материнскими,
                                аффилированными, дочерними компаниями и другими компаниями,
                                находящимися под общим управлением и в совместной собственности;
                            </li>
                            <li>с вашего согласия или согласно вашим указаниям; </li>
                            <li>
                                мы также можем передавать агрегированную или обезличенную
                                информацию, которая не может быть использована для установления
                                вашей личности.
                            </li>
                        </ul>
                        <h2>IV. Рекламные и аналитические сервисы</h2>
                        <p>
                            Мы можем разрешать другим лицам предоставлять рекламные и аналитические
                            сервисы от нашего имени в сети Интернет и мобильных приложениях. При
                            предоставлении таких сервисов могут использоваться файлы cookie,
                            веб-маяки, идентификаторы устройств и другие технологии сбора
                            информации, связанной с использованием вами Сервиса, включая ваш
                            IP-адрес, веб-браузер, сведения о мобильной сети, просмотренные
                            страницы, время, проведенное на страницах или в мобильных приложениях,
                            нажатые ссылки и сведения о переносе данных.
                        </p>
                        <h2>V. Ваши права на данные </h2>
                        <p>
                            В зависимости от действующего законодательства вы можете иметь следующие
                            права на ваши персональные данные, находящиеся в распоряжении BLAGO:
                        </p>
                        <ul>
                            <li>
                                Право на осведомленность: вы можете иметь право получать сведения о
                                категориях персональных данных, которые BLAGO собирает о вас и
                                которые также указаны в разделе «Сбор информации» выше.
                            </li>
                            <li>
                                Право на доступ: вы можете иметь право требовать сообщить вам: (i)
                                категории персональных данных, которые мы собрали о вас; (ii)
                                источники сбора персональных данных; (iii) цели такого сбора, (iv)
                                сведения о третьих лицах, которым мы предоставляем персональные
                                данные; и (v) какие ваши персональные данные мы предоставили другим
                                лицам и на каких основаниях.
                            </li>
                            <li>
                                Право на удаление: вы можете иметь право требовать от нас удаления
                                персональных данных, полученных от вас.
                            </li>
                            <li>
                                Право на отказ от продажи данных: BLAGO не «продает» ваши
                                персональные данные согласно определению, предусмотренному CCPA.
                            </li>
                            <li>
                                Право на предъявление иска: вы можете иметь право на предъявление
                                иска в соответствии с действующим законодательством, если нарушение
                                защиты данных оказывает на вас неблагоприятное воздействие.
                            </li>
                            <li>
                                Право на передачу данных: вы можете иметь право требовать
                                перемещения, копирования или передачи ваших личных данных,
                                находящихся в распоряжении BLAGO, в машиночитаемом формате.
                            </li>
                        </ul>
                        <p>
                            BLAGO может обрабатывать запросы, связанные с персональными данными,
                            собранными за предшествующие 12 месяцев, в соответствии с законодательно
                            установленными требованиями. До выполнения вашего запроса мы вправе
                            проверить вашу личность или наличие у агента полномочий выступать от
                            вашего имени. BLAGO не будет ущемлять ваши интересы по причине
                            использования вами прав на данные. Для направления запроса обратитесь к
                            нам, используя информацию, указанную ниже в разделе VII «Обратная
                            связь».
                        </p>
                        <h2>VI. Применимость к субъектам данных ЕС </h2>
                        <p>
                            BLAGO в качестве контролера данных обычно полагается на следующие
                            правовые основания обработки персональных данных: 
                        </p>
                        <ol>
                            <li>
                                 <strong>Предоставление Сервисов.</strong> Использование вашей
                                информации может быть необходимо для предоставления Сервисов.
                                Например, если вы используете веб-сайт BLAGO, мы будем использовать
                                вашу информацию для выполнения наших договорных обязательств или для
                                предоставления таких Сервисов. 
                            </li>
                            <li>
                                <strong>Законные интересы.</strong> Мы используем вашу информацию в
                                наших законных интересах, например, для предоставления вам
                                наилучшего контента на нашем веб-сайте, общения с пользователями и
                                общественностью, улучшения и продвижения наших продуктов и Сервисов,
                                а также для административных целей, в целях безопасности,
                                предотвращения мошенничества и для юридических целей. 
                            </li>
                            {/* <li>
                                <strong>Согласие.</strong> Мы можем полагаться на ваше согласие на
                                использование ваших персональных данных для определенных целей
                                прямого маркетинга, таких как направление вам актуальной информации
                                о наших продуктах и Сервисах. Вы вправе отозвать свое согласие в
                                любое время с помощью функции отказа от подписки, имеющейся в каждом
                                маркетинговом электронном письме, или обратившись к нам на адрес
                                электронной почты blagotruns@gmail.com. Вы понимаете, что после
                                отзыва вашего согласия мы больше не сможем предоставлять вам наши
                                продукты или определенные элементы Сервисов, в связи с чем вы
                                обязуетесь не предъявлять претензии, жалобы или иски против нас в
                                связи с невозможностью предоставления нами таких продуктов или
                                Сервисов. 
                            </li> */}
                            <li>
                                 <strong>Соблюдение юридического обязательства.</strong> Мы также
                                можем использовать вашу информацию по мере необходимости для
                                соблюдения положений действующего законодательства.
                            </li>
                        </ol>
                        <p>
                            Кроме того, вы как Субъект данных ЕС можете иметь следующие права в
                            отношении ваших персональных данных: 
                        </p>
                        <ul>
                            <li>
                                Право на доступ к вашим персональным данным для понимания того,
                                какими сведениями о вас мы обладаем 
                            </li>
                            <li>
                                Право на исправление неверных или неполных персональных данных о
                                вас, которыми мы обладаем 
                            </li>
                            <li>
                                Право на ограничение/приостановку обработки нами ваших персональных
                                данных 
                            </li>
                            <li>
                                Право на подачу претензии в надзорный орган, если вы считаете, что
                                ваши права на неприкосновенность личной информации нарушены. 
                            </li>
                        </ul>
                        <p>Дополнительные права, которые вы можете иметь в некоторых случаях: </p>
                        <ul>
                            <li>
                                Право на перенос данных (если обработка нами данных выполняется на
                                основании полученного согласия и осуществляется с помощью средств
                                автоматизации) 
                            </li>
                            <li>
                                Право в любое время на отзыв согласия (если обработка выполняется на
                                основании полученного согласия) 
                            </li>
                            <li>
                                Право на предъявление возражений против обработки (если обработка
                                выполняется на основании законных интересов) 
                            </li>
                            <li>
                                Право на предъявление возражений против обработки ваших персональных
                                данных в целях осуществления прямого маркетинга 
                            </li>
                            <li>
                                Право на удаление ваших персональных данных из нашей системы («право
                                на забвение») при наличии определенных оснований 
                            </li>
                        </ul>
                        {/* <p>
                            Субъекты данных ЕС для реализации своих прав на неприкосновенность
                            личной информации могут направить нам сообщение на адрес электронной
                            почты blagotruns@gmail.com, указав «Субъекты данных ЕС:
                            неприкосновенность личной информации» в строке темы. 
                        </p> */}
                        <p>
                            Как правило, мы храним персональные данные до тех пор, пока это
                            необходимо в целях, для которых осуществляется сбор таких данных, при
                            условии, что нам может потребоваться сохранять определенные персональные
                            данные после выполнения таких целей для соблюдения юридических
                            обязательств и обязательств по ведению финансовой отчетности. 
                        </p>
                        <p>
                            Мы можем в тот или иной момент времени передавать ваши персональные
                            данные нашим поставщикам услуг в объеме, необходимом для предоставления
                            Сервисов, которые вы запрашиваете у нас. До передачи ваших персональных
                            данных мы заключаем с такими поставщиками письменное соглашение на
                            условиях, не менее обременительных, чем условия настоящей Политики. До
                            передачи ваших персональных данных в другую страну в объеме,
                            предусмотренном Общим регламентом по защите данных, мы предпринимаем
                            соответствующие меры безопасности в соответствии с требованиями статьи
                            46 Общего регламента по защите данных. В той степени, в которой это
                            применимо, страны, в которые мы передаем персональные данные, будут
                            обеспечивать уровень защиты данных, сходный с уровнем защиты
                            персональных данных Субъектов данных ЕС при их обработке в странах ЕС; в
                            противном случае, мы предпримем дополнительные меры по защите таких
                            данных.
                        </p>
                        <p>
                            Мы не используем принцип автоматизированного принятия решений при
                            обработке ваших персональных данных.
                        </p>
                        {/* <h2>VI. Обратная связь</h2>
                        <p>
                            Для получения дополнительной информации об Уведомлении о
                            конфиденциальности BLAGO или для отправки запроса субъекта данных вы
                            можете обратиться на адрес электронной почты blagotruns@gmail.com.
                        </p> */}
                    </>
                )}
            </div>
            <Button onClick={handleClose}>{t("dialog.finishedButton")}</Button>
        </Popup>
    );
};

export default Privacy;
